// src/components/Modal.js
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import StepIndicator from './StepIndicator';
import PhotoAssembly from './PhotoAssembly';
import { isMobileDevice } from '../utils/deviceDetection';
import PaymentMethods from './PaymentMethods';
import { functions, auth } from '../firebase/firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { createBlobUrl } from '../utils/imageProxy';
import { 
  createOrUpdateClientOrder, 
  updatePaymentStatus, 
  trackDownload,
  logBehaviorEvent 
} from '../services/firestoreTracking';
import './Modal.css';

export default function Modal({
  isOpen,
  onClose,
  imageSrc,
  onUpload,
  onRetake,
  isScanning,
  scannedImageURL,
  sessionId,
  fileName,
}) {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [displayMode, setDisplayMode] = useState('single'); // 'single' or 'assembled'
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [assembledImageURL, setAssembledImageURL] = useState(null);
  const [isPhotoConfirmed, setIsPhotoConfirmed] = useState(false); // NEW: confirmation state
  const [selectedPhotoType, setSelectedPhotoType] = useState(null); // 'digital' or 'physical'
  const [imageLoadError, setImageLoadError] = useState(false); // Track if image failed to load
  const errorHandledRef = useRef(false); // Track if error has been handled to prevent infinite loops
  const [watermarkedImageURL, setWatermarkedImageURL] = useState(null); // Watermarked preview image
  const [isFinalizingFreeOrder, setIsFinalizingFreeOrder] = useState(false);
  const watermarkCanvasRef = useRef(null); // Canvas ref for watermarking
  const watermarkedUrlRef = useRef(null); // Ref to track current watermarked URL for cleanup
  
  // Customer information state
  const [customerInfo, setCustomerInfo] = useState({
    email: '',
    confirmEmail: '',
    fullName: '',
    address: '',
    city: '',
    postcode: '',
    country: '',
    promoCode: '',
    promoDiscount: 0,
  });
  const [isCustomerInfoComplete, setIsCustomerInfoComplete] = useState(false);
  const [isValidatingPromoCode, setIsValidatingPromoCode] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [promoCodeValid, setPromoCodeValid] = useState(false);
  const [showPromoCodeInput, setShowPromoCodeInput] = useState(false);

  // Detect mobile device and handle orientation changes
  useEffect(() => {
    setIsMobile(isMobileDevice());
    
    // Handle orientation changes
    const handleOrientationChange = () => {
      // Force a re-render when orientation changes
      setIsMobile(isMobileDevice());
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  // Reset all state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      setProgress(0);
      setIsPaymentComplete(false);
      setIsPhotoConfirmed(false);
      setSelectedPhotoType(null);
      setDisplayMode('single'); // Reset to single photo mode
      setImageLoadError(false);
      setAssembledImageURL(null); // Clear assembled image
      if (watermarkedUrlRef.current && watermarkedUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(watermarkedUrlRef.current);
        watermarkedUrlRef.current = null;
      }
      setWatermarkedImageURL(null); // Clear watermarked image
      setIsFinalizingFreeOrder(false);
      setCustomerInfo({
        email: '',
        confirmEmail: '',
        fullName: '',
        address: '',
        city: '',
        postcode: '',
        country: '',
        promoCode: '',
        promoDiscount: 0,
      });
      setIsCustomerInfoComplete(false);
      setIsValidatingPromoCode(false);
      setPromoCodeError('');
      setPromoCodeValid(false);
      setShowPromoCodeInput(false);
      errorHandledRef.current = false; // Reset error handler flag
    }
  }, [isOpen]);

  // Reset state when imageSrc changes (new photo selected)
  useEffect(() => {
    if (imageSrc) {
      // Reset all state variables when a new image is selected
      setStepIndex(0);
      setProgress(0);
      setIsPaymentComplete(false);
      setIsPhotoConfirmed(false);
      setSelectedPhotoType(null);
      setDisplayMode('single'); // Reset to single photo mode
      setImageLoadError(false);
      setAssembledImageURL(null); // Clear assembled image
      if (watermarkedUrlRef.current && watermarkedUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(watermarkedUrlRef.current);
        watermarkedUrlRef.current = null;
      }
      setWatermarkedImageURL(null); // Clear watermarked image
      setIsFinalizingFreeOrder(false);
      setCustomerInfo({
        email: '',
        confirmEmail: '',
        fullName: '',
        address: '',
        city: '',
        postcode: '',
        country: '',
        promoCode: '',
        promoDiscount: 0,
      });
      setIsCustomerInfoComplete(false);
      setIsValidatingPromoCode(false);
      setPromoCodeError('');
      setPromoCodeValid(false);
      setShowPromoCodeInput(false);
      errorHandledRef.current = false; // Reset error handler flag
    }
  }, [imageSrc]);
  
  // Reset image load error when scannedImageURL changes
  useEffect(() => {
    if (scannedImageURL) {
      setImageLoadError(false);
      errorHandledRef.current = false; // Reset error handler flag
    } else {
      // When scannedImageURL is cleared (new photo selected), also clear assembled image
      setAssembledImageURL(null);
      setImageLoadError(false);
      errorHandledRef.current = false;
    }
  }, [scannedImageURL]);
  
  // Reset error handler when modal opens or closes
  useEffect(() => {
    errorHandledRef.current = false;
    setImageLoadError(false);
  }, [isOpen]);

  // Function to create watermarked image
  const createWatermarkedImage = async (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      img.onerror = () => reject(new Error('Failed to load image for watermarking'));
      
      img.onload = () => {
        try {
          // Create canvas
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas size to match image
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw the original image
          ctx.drawImage(img, 0, 0);
          
          // Calculate watermark position and size
          const fontSize = Math.max(14, img.width * 0.025); // Responsive font size
          const padding = fontSize * 1.2;
          const text = 'Procesado por FOTOPACASA.ES';
          
          ctx.font = `600 ${fontSize}px 'SF Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Measure text
          const textMetrics = ctx.measureText(text);
          const textWidth = textMetrics.width;
          const textHeight = fontSize;
          
          // Calculate position (bottom center, 8% from bottom)
          const x = canvas.width / 2;
          const y = canvas.height * 0.92;
          
          // Draw full-width horizontal rectangle banner
          const bgPaddingY = padding * 0.6;
          const bgX = 0; // Start from left edge
          const bgY = y - textHeight / 2 - bgPaddingY;
          const bgWidth = canvas.width; // Full width
          const bgHeight = textHeight + bgPaddingY * 2;
          
          // Draw full-width rectangle background with shadow
          ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
          ctx.shadowBlur = 18;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 8;
          
          ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
          // Draw full-width rectangle (no rounded corners)
          ctx.fillRect(bgX, bgY, bgWidth, bgHeight);
          
          // Reset shadow for text
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          
          // Draw text (letter spacing not supported in canvas, so we'll use a slightly different approach)
          ctx.fillStyle = 'rgba(35, 34, 33, 0.75)';
          // Draw text with manual spacing by drawing each character separately
          const chars = text.split('');
          let charX = x - textWidth / 2;
          const charSpacing = fontSize * 0.12; // Approximate letter spacing
          chars.forEach((char, index) => {
            ctx.fillText(char, charX, y);
            const charWidth = ctx.measureText(char).width;
            charX += charWidth + (index < chars.length - 1 ? charSpacing : 0);
          });
          
          // Convert to blob URL
          canvas.toBlob((blob) => {
            if (blob) {
              const blobUrl = URL.createObjectURL(blob);
              resolve(blobUrl);
            } else {
              reject(new Error('Failed to create watermarked image'));
            }
          }, 'image/jpeg', 0.95);
        } catch (error) {
          reject(error);
        }
      };
      
      img.src = imageUrl;
    });
  };

  // Generate watermarked image when processed image is available and payment is not complete
  useEffect(() => {
    // Clean up previous watermarked image
    if (watermarkedUrlRef.current && watermarkedUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(watermarkedUrlRef.current);
      watermarkedUrlRef.current = null;
    }
    
    if (scannedImageURL && !imageLoadError && !isPaymentComplete && !isScanning) {
      // Create watermarked version
      createWatermarkedImage(scannedImageURL)
        .then((watermarkedUrl) => {
          watermarkedUrlRef.current = watermarkedUrl;
          setWatermarkedImageURL(watermarkedUrl);
        })
        .catch((error) => {
          console.error('Error creating watermarked image:', error);
          // Fallback to original if watermarking fails
          watermarkedUrlRef.current = null;
          setWatermarkedImageURL(null);
        });
    } else {
      // Clean up watermarked image when not needed
      watermarkedUrlRef.current = null;
      setWatermarkedImageURL(null);
    }
    
    // Cleanup on unmount or when dependencies change
    return () => {
      if (watermarkedUrlRef.current && watermarkedUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(watermarkedUrlRef.current);
        watermarkedUrlRef.current = null;
      }
    };
  }, [scannedImageURL, imageLoadError, isPaymentComplete, isScanning]);

  useEffect(() => {
    if (isScanning) {
      // In development mode, use shorter duration for faster testing
      // In production, use longer duration to match actual processing time
      const isDevMode = process.env.NODE_ENV === 'development';
      const totalDuration = isDevMode 
        ? (isMobile ? 3000 : 2000) // 2-3 seconds in dev mode
        : (isMobile ? 35000 : 30000); // 30-35 seconds in production
      
      const stepsCount = 4;
      const stepInterval = totalDuration / (stepsCount - 1);

      const stepTimer = setInterval(() => {
        setStepIndex((i) => Math.min(i + 1, stepsCount - 1));
      }, stepInterval);

      const startTime = Date.now();
      
      // Reduce update frequency on mobile for better performance
      const updateInterval = isMobile ? 500 : 200;
      
      const progTimer = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const pct = Math.min(100, (elapsed / totalDuration) * 100);
        setProgress(pct);
        if (pct >= 100) {
          clearInterval(progTimer);
          // In dev mode, animation completes faster but parent controls isScanning state
          // The animation will just finish quicker, allowing faster testing
        }
      }, updateInterval);

      return () => {
        clearInterval(stepTimer);
        clearInterval(progTimer);
      };
    } else {
      setStepIndex(0);
      setProgress(0);
    }
  }, [isScanning, isMobile]);

  const getBasePrice = (photoType = selectedPhotoType) =>
    photoType === 'digital' ? 0.99 : 4.99;

  const getAppliedPromoDiscount = () =>
    promoCodeValid ? Number(customerInfo.promoDiscount || 0) : 0;

  const getFinalPaymentAmount = (photoType = selectedPhotoType) => {
    const basePrice = getBasePrice(photoType);
    const discount = getAppliedPromoDiscount();
    return Number(
      Math.max(0, basePrice * (1 - discount / 100)).toFixed(2)
    );
  };

  const handlePaymentSuccess = async (order) => {
    console.log("Pago completado correctamente:", order);
    setIsPaymentComplete(true);

    // Update payment status in Firestore
    if (auth.currentUser) {
      const currentSessionId = sessionId || sessionStorage.getItem('sessionId');
      const currentFileName = fileName || 'unknown';
      const paymentAmount = Number.parseFloat(
        String(order.amount ?? getFinalPaymentAmount())
      );
      const normalizedPaymentAmount = Number.isFinite(paymentAmount)
        ? paymentAmount
        : getFinalPaymentAmount();
      const promoDiscount = getAppliedPromoDiscount();
      const normalizedPromoCode = (customerInfo.promoCode || '').trim();
      const providerOrderId =
        order?.gatewayResponse?.order ||
        order?.gatewayResponse?.details?.order ||
        order?.order ||
        '';
      
      await updatePaymentStatus(currentSessionId, 'completed', {
        userId: auth.currentUser.uid,
        paymentMethod: order.paymentMethod || 'unknown',
        amount: normalizedPaymentAmount,
      });

      if (normalizedPromoCode && promoDiscount > 0) {
        try {
          const redeemPromoCode = httpsCallable(functions, 'redeemPromoCode');
          await redeemPromoCode({
            promoCode: normalizedPromoCode,
            email: customerInfo.email || undefined,
            userInfo: selectedPhotoType === 'physical' ? {
              fullName: customerInfo.fullName,
              address: customerInfo.address,
              city: customerInfo.city,
              postcode: customerInfo.postcode,
              country: customerInfo.country,
            } : undefined,
            photoType: selectedPhotoType,
            orderId: providerOrderId || currentSessionId,
            sessionId: currentSessionId,
          });
        } catch (error) {
          console.error('Error redeeming promo code after payment:', error);
        }
      }

      // Update client order with payment info
      await createOrUpdateClientOrder({
        userId: auth.currentUser.uid,
        sessionId: currentSessionId,
        email: customerInfo.email,
        fullName: customerInfo.fullName,
        address: customerInfo.address,
        city: customerInfo.city,
        postcode: customerInfo.postcode,
        country: customerInfo.country || 'Spain',
        photoType: selectedPhotoType,
        uploadedImagePath: `uploads/${auth.currentUser.uid}/${currentFileName}.jpg`,
        processedImagePath: scannedImageURL ? `downloads/${auth.currentUser.uid}/${currentFileName}_portrait.jpg` : null,
        promoCode: customerInfo.promoCode || null,
        promoDiscount: promoDiscount,
        paymentStatus: 'completed',
        paymentMethod: order.paymentMethod || 'unknown',
        paymentAmount: normalizedPaymentAmount,
      });
    }
  };

  // Download handler for digital photos
  const handleDownload = async (e, mode = null) => {
    if (e) {
      e.preventDefault();
    }
    
    // Use the provided mode or current displayMode
    const downloadMode = mode || displayMode;
    const imageUrl = downloadMode === "single" ? scannedImageURL : assembledImageURL;
    const filename = downloadMode === "single" ? "foto_carnet.jpg" : "plantilla_10x15.jpg";
    
    if (!imageUrl) {
      alert('La imagen aún no está lista. Por favor, espera un momento.');
      return;
    }

    try {
      // For blob URLs or data URLs, we can download directly
      if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // For remote URLs (especially Firebase Storage), use the CORS-safe blob URL creator
      const blobUrl = await createBlobUrl(
        imageUrl,
        downloadMode === "single" ? imageSrc : null,
        false,
        { allowDirectUrlOnMobile: false }
      );
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay to ensure download starts
      setTimeout(() => {
        if (blobUrl.startsWith('blob:')) {
          window.URL.revokeObjectURL(blobUrl);
        }
      }, 100);

      // Track download
      if (auth.currentUser) {
        const currentSessionId = sessionId || sessionStorage.getItem('sessionId');
        const currentFileName = fileName || 'unknown';
        const downloadPath = downloadMode === 'single' 
          ? `downloads/${auth.currentUser.uid}/${currentFileName}_portrait.jpg`
          : `downloads/${auth.currentUser.uid}/${currentFileName}_assembled.jpg`;
        
        trackDownload(currentFileName, downloadPath, currentSessionId);
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      alert('Error al descargar la imagen. Por favor, intenta de nuevo.');
    }
  };

  // Handler for display mode buttons that directly downloads
  const handleDisplayModeAndDownload = async (mode) => {
    // Check if image is ready before downloading
    const imageUrl = mode === "single" ? scannedImageURL : assembledImageURL;
    
    if (!imageUrl) {
      // If image is not ready, just set the mode and show alert
      setDisplayMode(mode);
      if (mode === 'assembled' && !assembledImageURL) {
        alert('La plantilla aún no está lista. Por favor, espera un momento.');
      } else {
        alert('La imagen aún no está lista. Por favor, espera un momento.');
      }
      return;
    }
    
    // Set the mode and download immediately
    setDisplayMode(mode);
    // Small delay to ensure state is updated, then download
    setTimeout(() => {
      handleDownload(null, mode);
    }, 50);
  };

  const handlePaymentError = (error) => {
    console.error("El pago ha fallado:", error);
    // You might want to show an error message to the user
  };

  const handleCustomerInfoChange = (field, value) => {
    const shouldResetPromoValidation = ['promoCode', 'email', 'fullName', 'postcode'].includes(field);
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value,
      ...(shouldResetPromoValidation ? { promoDiscount: 0 } : {}),
    }));
    if (shouldResetPromoValidation) {
      setPromoCodeError('');
      setPromoCodeValid(false);
    }
  };

  // Separate function to validate only the promo code
  const validatePromoCodeOnly = async () => {
    if (!customerInfo.promoCode || customerInfo.promoCode.trim() === '') {
      setPromoCodeError('Por favor, introduce un código promocional');
      return;
    }

    setIsValidatingPromoCode(true);
    setPromoCodeError('');
    setPromoCodeValid(false);

    try {
      const validatePromoCode = httpsCallable(functions, 'validatePromoCode');
      const result = await validatePromoCode({ 
        promoCode: customerInfo.promoCode,
        email: customerInfo.email || undefined,
        userInfo: selectedPhotoType === 'physical' ? {
          fullName: customerInfo.fullName,
          address: customerInfo.address,
          city: customerInfo.city,
          postcode: customerInfo.postcode,
          country: customerInfo.country
        } : undefined,
        photoType: selectedPhotoType
      });
      const data = result.data;
      
      if (data.valid) {
        setPromoCodeValid(true);
        setPromoCodeError('');
        setCustomerInfo(prev => ({
          ...prev,
          promoCode: data.code || prev.promoCode.trim().toUpperCase(),
          promoDiscount: data.discount || 0
        }));
      } else {
        setPromoCodeError(data.error || 'Código promocional no válido');
        setPromoCodeValid(false);
        setCustomerInfo(prev => ({
          ...prev,
          promoDiscount: 0
        }));
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoCodeError('Error al validar el código promocional. Por favor, intenta de nuevo.');
      setPromoCodeValid(false);
      setCustomerInfo(prev => ({
        ...prev,
        promoDiscount: 0
      }));
    } finally {
      setIsValidatingPromoCode(false);
    }
  };

  const validateAndSubmitCustomerInfo = async () => {
    // Clear previous promo code errors
    setPromoCodeError('');

    if (selectedPhotoType === 'digital') {
      // Validate email fields
      if (!customerInfo.email || !customerInfo.confirmEmail) {
        alert('Por favor, completa todos los campos');
        return;
      }
      if (customerInfo.email !== customerInfo.confirmEmail) {
        alert('Los emails no coinciden');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerInfo.email)) {
        alert('Por favor, introduce un email válido');
        return;
      }
    } else if (selectedPhotoType === 'physical') {
      // Validate address fields
      if (!customerInfo.fullName || !customerInfo.address || !customerInfo.city || !customerInfo.postcode) {
        alert('Por favor, completa todos los campos obligatorios');
        return;
      }
      // Validate email fields for physical photos
      if (!customerInfo.email || !customerInfo.confirmEmail) {
        alert('Por favor, completa todos los campos');
        return;
      }
      if (customerInfo.email !== customerInfo.confirmEmail) {
        alert('Los emails no coinciden');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerInfo.email)) {
        alert('Por favor, introduce un email válido');
        return;
      }
    }

    const finalPaymentAmount = getFinalPaymentAmount(selectedPhotoType);

    // Validate promo code if provided and not already validated
    if (customerInfo.promoCode && customerInfo.promoCode.trim() !== '') {
      if (!promoCodeValid) {
        // Promo code exists but hasn't been validated yet
        alert('Por favor, valida el código promocional antes de continuar');
        return;
      }
      // Promo code is already validated, proceed
      setIsCustomerInfoComplete(true);
    } else {
      // No promo code provided, proceed normally
      setIsCustomerInfoComplete(true);
    }

    if (finalPaymentAmount <= 0) {
      const currentSessionId = sessionId || sessionStorage.getItem('sessionId');
      setIsFinalizingFreeOrder(true);

      try {
        await handlePaymentSuccess({
          paymentMethod: 'PROMO_CODE',
          amount: 0,
          currency: 'EUR',
          status: 'completed',
          order: currentSessionId || '',
          gatewayResponse: {
            gateway: 'internal',
            bypassedPayment: true,
            order: currentSessionId || '',
          },
        });
      } catch (error) {
        console.error('Error finalizing zero-total order:', error);
        alert('No se ha podido finalizar el pedido gratuito. Inténtalo de nuevo.');
      } finally {
        setIsFinalizingFreeOrder(false);
      }

      return;
    }

    // Save customer info to Firestore (will be updated with payment info later)
    if (auth.currentUser) {
      const currentSessionId = sessionId || sessionStorage.getItem('sessionId');
      const currentFileName = fileName || 'unknown';
      
      const promoDiscount = getAppliedPromoDiscount();
      
      createOrUpdateClientOrder({
        userId: auth.currentUser.uid,
        sessionId: currentSessionId,
        email: customerInfo.email,
        fullName: customerInfo.fullName,
        address: customerInfo.address,
        city: customerInfo.city,
        postcode: customerInfo.postcode,
        country: customerInfo.country || 'Spain',
        photoType: selectedPhotoType,
        uploadedImagePath: `uploads/${auth.currentUser.uid}/${currentFileName}.jpg`,
        processedImagePath: scannedImageURL ? `downloads/${auth.currentUser.uid}/${currentFileName}_portrait.jpg` : null,
        promoCode: customerInfo.promoCode || null,
        promoDiscount: promoDiscount,
        paymentStatus: 'pending',
      }).catch(error => {
        console.error('Error saving customer info:', error);
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isMobile ? 'mobile-modal' : ''} modal-scrollable-content`}>
        
        {!isScanning && !(scannedImageURL && isPhotoConfirmed && selectedPhotoType && isCustomerInfoComplete && !isPaymentComplete) && (
          <button
            className="modal-close-icon"
            onClick={onClose}
          >
            ×
          </button>
        )}

        {/* Only show step indicator on desktop devices to save resources */}
        {isScanning && !isMobile && <StepIndicator current={stepIndex} />}

        {/* Photo title section - hide when address/email forms are shown */}
        {isScanning || !selectedPhotoType || isCustomerInfoComplete ? (
          <>
            <div style={{
              width: '100%',
              maxWidth: '343px',
              margin: '0 auto 20px',
              textAlign: 'center'
            }}>
              <div style={{
                padding: '10px',
                fontSize: '32px',
                fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: '700',
                color: 'black',
                lineHeight: '1.2'
              }}>
                Foto
              </div>
            </div>

            <div className="image-container">
              {isScanning ? (
                <>
                  <img
                    className="uploaded-image"
                    src={imageSrc}
                    alt="Procesando..."
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      transition: 'all 0.3s ease-in-out',
                      margin: 'auto'
                    }}
                  />
                  <div className={`scanning-animation ${isMobile ? 'mobile-scanning' : ''}`}>
                    <div className="scan-line" />
                  </div>
                </>
              ) : (
                <>
                  {displayMode === 'single' ? (
                    <>
                    <img
                      className="processed-image protected-image"
                      src={
                        imageLoadError || !scannedImageURL 
                          ? imageSrc 
                          : (watermarkedImageURL && !isPaymentComplete ? watermarkedImageURL : scannedImageURL)
                      }
                      alt="Resultado"
                      crossOrigin={
                        scannedImageURL && 
                        !imageLoadError && 
                        !scannedImageURL.startsWith('blob:') && 
                        !scannedImageURL.startsWith('data:') &&
                        scannedImageURL.includes('firebasestorage.googleapis.com') ? "anonymous" : undefined
                      }
                      // Add referrerPolicy to help with adblockers
                      referrerPolicy="no-referrer"
                      key={`img-${errorHandledRef.current ? 'error' : scannedImageURL ? 'processed' : 'original'}-${imageLoadError}-${watermarkedImageURL ? 'watermarked' : ''}`}
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                      draggable={false}
                      onError={(e) => {
                        // Use a static flag to prevent multiple error logs
                        const errorTarget = e.target;
                        const errorKey = errorTarget.dataset.errorHandled;
                        
                        // Check if this specific image element has already had its error handled
                        if (errorKey === 'true' || errorHandledRef.current) {
                          // Error already handled for this image - silently ignore
                          errorTarget.onerror = null;
                          return false;
                        }
                        
                        // If we're already showing fallback image, ignore errors
                        if (imageLoadError || !scannedImageURL) {
                          errorTarget.onerror = null;
                          errorTarget.dataset.errorHandled = 'true';
                          return false;
                        }
                        
                        // Mark this image element as having its error handled
                        errorTarget.dataset.errorHandled = 'true';
                        errorHandledRef.current = true;
                        
                        // Only log once
                        const isDevMode = process.env.NODE_ENV === 'development';
                        if (isDevMode) {
                          // Only log the first error, then silence all subsequent ones
                          console.warn("Error al cargar la imagen procesada. Usando imagen original.");
                        }
                        
                        // Disable error handler immediately
                        errorTarget.onerror = null;
                        
                        // Set error state to switch to fallback
                        // Use setTimeout to batch the state update
                        setTimeout(() => {
                          setImageLoadError(true);
                        }, 0);
                        
                        return false;
                      }}
                      onLoad={(e) => {
                        // Reset error state if image loads successfully
                        if (imageLoadError) {
                          setImageLoadError(false);
                          errorHandledRef.current = false;
                        }
                        // Mark image as loaded successfully
                        if (e.target) {
                          e.target.dataset.errorHandled = 'false';
                        }
                      }}
                      data-error-handled="false"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        transition: 'all 0.3s ease-in-out',
                        margin: 'auto',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        pointerEvents: 'auto'
                      }}
                    />
                    {!isScanning && scannedImageURL && !imageLoadError && !isPaymentComplete && watermarkedImageURL && (
                      <div className="image-protection-overlay" aria-hidden="true" />
                    )}
                    </>
                  ) : (
                    <PhotoAssembly 
                      processedImageURL={scannedImageURL}
                      fallbackImageURL={imageSrc}
                      onReady={setAssembledImageURL}
                    />
                  )}
                </>
              )}
            </div>
          </>
        ) : null}

        {isScanning ? (
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
            <p style={{ textAlign: 'center', marginTop: '10px' }}>
              {isMobile ? t('processing') : t('processingPhoto')}
            </p>
          </div>
        ) : (
          <div className={`button-container ${isMobile ? 'mobile-buttons' : ''}`}>
            {scannedImageURL && !isPhotoConfirmed && (
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <button
                  style={{
                    minWidth: isMobile ? '120px' : '140px',
                    height: '52px',
                    padding: '12px 16px',
                    background: '#232221',
                    border: 'none',
                    borderRadius: '999px',
                    color: 'white',
                    fontSize: '14px',
                    fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontWeight: '700',
                    lineHeight: '28px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '7px',
                    boxShadow: '0px 4px 8px rgba(35, 34, 33, 0.4)',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setIsPhotoConfirmed(true)}
                  onMouseOver={(e) => {
                    e.target.style.background = '#1A1A1A';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0px 6px 12px rgba(35, 34, 33, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#232221';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0px 4px 8px rgba(35, 34, 33, 0.4)';
                  }}
                >
                  {t('confirmPhoto') || 'Next Step'}
                </button>
              </div>
            )}
            {scannedImageURL && isPhotoConfirmed && !selectedPhotoType && (
              <div style={{ width: '100%', maxWidth: '343px', margin: '0 auto' }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: 'black',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  Selecciona tu tipo de foto
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  {/* Digital Photo Button */}
                  <button
                    onClick={() => setSelectedPhotoType('digital')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      borderRadius: '999px',
                      border: 'none',
                      fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: '700',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      height: '60px',
                      background: '#FFD275',
                      color: '#212121',
                      boxShadow: '0px 4px 8px rgba(35, 34, 33, 0.2)'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#FFE08F';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0px 6px 12px rgba(35, 34, 33, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = '#FFD275';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0px 4px 8px rgba(35, 34, 33, 0.2)';
                    }}
                    onMouseDown={(e) => {
                      e.target.style.transform = 'translateY(0)';
                    }}
                    onMouseUp={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontWeight: '700', fontSize: '16px', lineHeight: '1.2' }}>Foto Digital</span>
                    </div>
                  </button>

                  {/* Physical Photo Button */}
                  <button
                    onClick={() => setSelectedPhotoType('physical')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      borderRadius: '999px',
                      border: 'none',
                      fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: '700',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      height: '60px',
                      background: '#232221',
                      color: 'white',
                      boxShadow: '0px 4px 8px rgba(35, 34, 33, 0.2)'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#1a1a1a';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0px 6px 12px rgba(35, 34, 33, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = '#232221';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0px 4px 8px rgba(35, 34, 33, 0.2)';
                    }}
                    onMouseDown={(e) => {
                      e.target.style.transform = 'translateY(0)';
                    }}
                    onMouseUp={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontWeight: '700', fontSize: '16px', lineHeight: '1.2' }}>Físico</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
            {scannedImageURL && isPhotoConfirmed && selectedPhotoType && !isCustomerInfoComplete && (
              <div style={{ width: '100%', maxWidth: '343px', margin: '0 auto' }}>
                {selectedPhotoType === 'digital' ? (
                  // Email form for digital photos
                  <div>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: 'black',
                      marginBottom: '16px',
                      textAlign: 'center'
                    }}>
                      Información de contacto
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <input
                        type="email"
                        placeholder="Correo electrónico"
                        value={customerInfo.email}
                        onChange={(e) => handleCustomerInfoChange('email', e.target.value)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                          fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                      />
                      <input
                        type="email"
                        placeholder="Confirmar correo electrónico"
                        value={customerInfo.confirmEmail}
                        onChange={(e) => handleCustomerInfoChange('confirmEmail', e.target.value)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                          fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                      />
                      {showPromoCodeInput && (
                        <>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <input
                              type="text"
                              placeholder="Código promocional"
                              value={customerInfo.promoCode}
                              onChange={(e) => handleCustomerInfoChange('promoCode', e.target.value)}
                              style={{
                                flex: 1,
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: promoCodeError ? '1px solid #e52e71' : promoCodeValid ? '1px solid #4CAF50' : '1px solid #ddd',
                                fontSize: '16px',
                                fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                              }}
                            />
                            <button
                              type="button"
                              onClick={validatePromoCodeOnly}
                              disabled={isValidatingPromoCode}
                              style={{
                                padding: '12px 20px',
                                borderRadius: '8px',
                                border: 'none',
                                background: isValidatingPromoCode ? '#999' : '#232221',
                                color: 'white',
                                fontSize: '14px',
                                fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                fontWeight: '600',
                                cursor: isValidatingPromoCode ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease',
                                opacity: isValidatingPromoCode ? 0.7 : 1
                              }}
                              onMouseOver={(e) => {
                                if (!isValidatingPromoCode) {
                                  e.target.style.background = '#1A1A1A';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (!isValidatingPromoCode) {
                                  e.target.style.background = '#232221';
                                }
                              }}
                            >
                              {isValidatingPromoCode ? 'Validando...' : 'Validar'}
                            </button>
                          </div>
                          {promoCodeError && (
                            <div style={{
                              color: '#e52e71',
                              fontSize: '14px',
                              marginTop: '-8px',
                              paddingLeft: '4px'
                            }}>
                              {promoCodeError}
                            </div>
                          )}
                          {promoCodeValid && !promoCodeError && (
                            <div style={{
                              color: '#4CAF50',
                              fontSize: '14px',
                              marginTop: '-8px',
                              paddingLeft: '4px'
                            }}>
                              ✓ {customerInfo.promoDiscount}% descuento aplicado — Precio final: €{(0.99 * (1 - (customerInfo.promoDiscount || 0) / 100)).toFixed(2)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  // Address form for physical photos
                  <div>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: 'black',
                      marginBottom: '16px',
                      textAlign: 'center'
                    }}>
                      Información de envío
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <input
                        type="text"
                        placeholder="Nombre completo *"
                        value={customerInfo.fullName}
                        onChange={(e) => handleCustomerInfoChange('fullName', e.target.value)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                          fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Dirección *"
                        value={customerInfo.address}
                        onChange={(e) => handleCustomerInfoChange('address', e.target.value)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                          fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Ciudad *"
                        value={customerInfo.city}
                        onChange={(e) => handleCustomerInfoChange('city', e.target.value)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                          fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Código postal *"
                        value={customerInfo.postcode}
                        onChange={(e) => handleCustomerInfoChange('postcode', e.target.value)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                          fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                      />
                      <input
                        type="email"
                        placeholder="Correo electrónico *"
                        value={customerInfo.email}
                        onChange={(e) => handleCustomerInfoChange('email', e.target.value)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                          fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                      />
                      <input
                        type="email"
                        placeholder="Confirmar correo electrónico *"
                        value={customerInfo.confirmEmail}
                        onChange={(e) => handleCustomerInfoChange('confirmEmail', e.target.value)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                          fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}
                      />
                      {showPromoCodeInput && (
                        <>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <input
                              type="text"
                              placeholder="Código promocional"
                              value={customerInfo.promoCode}
                              onChange={(e) => handleCustomerInfoChange('promoCode', e.target.value)}
                              style={{
                                flex: 1,
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: promoCodeError ? '1px solid #e52e71' : promoCodeValid ? '1px solid #4CAF50' : '1px solid #ddd',
                                fontSize: '16px',
                                fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                              }}
                            />
                            <button
                              type="button"
                              onClick={validatePromoCodeOnly}
                              disabled={isValidatingPromoCode}
                              style={{
                                padding: '12px 20px',
                                borderRadius: '8px',
                                border: 'none',
                                background: isValidatingPromoCode ? '#999' : '#232221',
                                color: 'white',
                                fontSize: '14px',
                                fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                fontWeight: '600',
                                cursor: isValidatingPromoCode ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease',
                                opacity: isValidatingPromoCode ? 0.7 : 1
                              }}
                              onMouseOver={(e) => {
                                if (!isValidatingPromoCode) {
                                  e.target.style.background = '#1A1A1A';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (!isValidatingPromoCode) {
                                  e.target.style.background = '#232221';
                                }
                              }}
                            >
                              {isValidatingPromoCode ? 'Validando...' : 'Validar'}
                            </button>
                          </div>
                          {promoCodeError && (
                            <div style={{
                              color: '#e52e71',
                              fontSize: '14px',
                              marginTop: '-8px',
                              paddingLeft: '4px'
                            }}>
                              {promoCodeError}
                            </div>
                          )}
                          {promoCodeValid && !promoCodeError && (
                            <div style={{
                              color: '#4CAF50',
                              fontSize: '14px',
                              marginTop: '-8px',
                              paddingLeft: '4px'
                            }}>
                              ✓ {customerInfo.promoDiscount}% descuento aplicado — Precio final: €{(4.99 * (1 - (customerInfo.promoDiscount || 0) / 100)).toFixed(2)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
                  <button
                    onClick={validateAndSubmitCustomerInfo}
                    disabled={isValidatingPromoCode}
                    style={{
                      width: '100%',
                      height: '52px',
                      padding: '12px 16px',
                      background: isValidatingPromoCode ? '#666' : '#232221',
                      border: 'none',
                      borderRadius: '999px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: '700',
                      lineHeight: '28px',
                      cursor: isValidatingPromoCode ? 'not-allowed' : 'pointer',
                      boxShadow: '0px 4px 8px rgba(35, 34, 33, 0.4)',
                      transition: 'all 0.2s ease',
                      opacity: isValidatingPromoCode ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!isValidatingPromoCode) {
                        e.target.style.background = '#1A1A1A';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0px 6px 12px rgba(35, 34, 33, 0.5)';
                      } else {
                        e.target.style.background = '#666';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isValidatingPromoCode) {
                        e.target.style.background = '#232221';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0px 4px 8px rgba(35, 34, 33, 0.4)';
                      } else {
                        e.target.style.background = '#666';
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {isValidatingPromoCode ? 'Validando código...' : 'Continuar '}
                  </button>
                  <button
                    onClick={() => setSelectedPhotoType(null)}
                    style={{
                      width: '100%',
                      height: '52px',
                      padding: '12px 16px',
                      background: 'transparent',
                      border: '1px solid #232221',
                      borderRadius: '999px',
                      color: '#232221',
                      fontSize: '14px',
                      fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: '700',
                      lineHeight: '28px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'rgba(35, 34, 33, 0.1)';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Volver
                  </button>
                  {!showPromoCodeInput && (
                    <button
                      type="button"
                      onClick={() => setShowPromoCodeInput(true)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid rgb(221, 221, 221)',
                        background: 'transparent',
                        fontSize: '14px',
                        fontFamily: '"SF Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        color: 'rgb(102, 102, 102)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        opacity: 0.7,
                        transition: 'all 0.2s ease 0s'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.opacity = '1';
                        e.target.style.color = '#333';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.opacity = '0.7';
                        e.target.style.color = 'rgb(102, 102, 102)';
                      }}
                    >
                      Añadir código promocional
                    </button>
                  )}
                </div>
              </div>
            )}
            {scannedImageURL && isPhotoConfirmed && selectedPhotoType && isCustomerInfoComplete && !isPaymentComplete && getFinalPaymentAmount(selectedPhotoType) > 0 && (
              <PaymentMethods
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                amount={getFinalPaymentAmount(selectedPhotoType).toFixed(2)}
              />
            )}
            {scannedImageURL && isPhotoConfirmed && selectedPhotoType && isCustomerInfoComplete && !isPaymentComplete && getFinalPaymentAmount(selectedPhotoType) <= 0 && isFinalizingFreeOrder && (
              <div style={{ width: '100%', maxWidth: '343px', margin: '0 auto', textAlign: 'center' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'black', marginBottom: '12px' }}>
                  Finalizando pedido
                </h3>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                  El total es 0,00 €. Estamos completando tu pedido sin pasar por el pago.
                </p>
              </div>
            )}
            {scannedImageURL && isPaymentComplete && selectedPhotoType === 'digital' && (
              <div className="display-options" style={{ width: '100%', maxWidth: '343px', margin: '0 auto' }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: 'black',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  Descargar tu foto
                </h3>
                <p style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '16px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#666'
                }}>
                  {t('displayOptions')}
                </p>
                <div className="display-buttons" style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  marginBottom: '16px',
                  justifyContent: 'center'
                }}>
                  <button
                    style={{
                      minWidth: isMobile ? '120px' : '140px',
                      height: '52px',
                      padding: '12px 16px',
                      background: displayMode === 'single' ? '#232221' : '#FFD275',
                      border: 'none',
                      borderRadius: '999px',
                      color: displayMode === 'single' ? 'white' : '#232221',
                      fontSize: '14px',
                      fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: '700',
                      lineHeight: '28px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '7px',
                      boxShadow: displayMode === 'single' ? '0px 4px 8px rgba(35, 34, 33, 0.4)' : '0px 4px 8px rgba(255, 210, 117, 0.4)',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleDisplayModeAndDownload('single')}
                    onMouseOver={(e) => {
                      e.target.style.background = displayMode === 'single' ? '#1A1A1A' : '#FFC94A';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = displayMode === 'single' ? '0px 6px 12px rgba(35, 34, 33, 0.5)' : '0px 6px 12px rgba(255, 210, 117, 0.5)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = displayMode === 'single' ? '#232221' : '#FFD275';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = displayMode === 'single' ? '0px 4px 8px rgba(35, 34, 33, 0.4)' : '0px 4px 8px rgba(255, 210, 117, 0.4)';
                    }}
                  >
                    {t('singlePhoto') || 'Foto Individual'}
                  </button>
                  <button
                    style={{
                      minWidth: isMobile ? '120px' : '140px',
                      height: '52px',
                      padding: '12px 16px',
                      background: displayMode === 'assembled' ? '#232221' : '#FFD275',
                      border: 'none',
                      borderRadius: '999px',
                      color: displayMode === 'assembled' ? 'white' : '#232221',
                      fontSize: '14px',
                      fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: '700',
                      lineHeight: '28px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '7px',
                      boxShadow: displayMode === 'assembled' ? '0px 4px 8px rgba(35, 34, 33, 0.4)' : '0px 4px 8px rgba(255, 210, 117, 0.4)',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleDisplayModeAndDownload('assembled')}
                    onMouseOver={(e) => {
                      e.target.style.background = displayMode === 'assembled' ? '#1A1A1A' : '#FFC94A';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = displayMode === 'assembled' ? '0px 6px 12px rgba(35, 34, 33, 0.5)' : '0px 6px 12px rgba(255, 210, 117, 0.5)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = displayMode === 'assembled' ? '#232221' : '#FFD275';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = displayMode === 'assembled' ? '0px 4px 8px rgba(35, 34, 33, 0.4)' : '0px 4px 8px rgba(255, 210, 117, 0.4)';
                    }}
                  >
                    {t('assembledPhotos') || 'Múltiples Fotos'}
                  </button>
                </div>
              </div>
            )}
            {scannedImageURL && isPaymentComplete && selectedPhotoType === 'physical' && (
              <div className="display-options" style={{ width: '100%', maxWidth: '343px', margin: '0 auto' }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: 'black',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  Descargar tu foto
                </h3>
                <p style={{ 
                  fontWeight: 'normal', 
                  marginBottom: '20px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#666',
                  lineHeight: '1.5'
                }}>
                  Tu foto física será enviada pronto a tu buzón de correo
                </p>
                <p style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '16px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#666'
                }}>
                  {t('displayOptions')}
                </p>
                <div className="display-buttons" style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  marginBottom: '16px',
                  justifyContent: 'center'
                }}>
                  <button
                    style={{
                      minWidth: isMobile ? '120px' : '140px',
                      height: '52px',
                      padding: '12px 16px',
                      background: displayMode === 'single' ? '#232221' : '#FFD275',
                      border: 'none',
                      borderRadius: '999px',
                      color: displayMode === 'single' ? 'white' : '#232221',
                      fontSize: '14px',
                      fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: '700',
                      lineHeight: '28px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '7px',
                      boxShadow: displayMode === 'single' ? '0px 4px 8px rgba(35, 34, 33, 0.4)' : '0px 4px 8px rgba(255, 210, 117, 0.4)',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleDisplayModeAndDownload('single')}
                    onMouseOver={(e) => {
                      e.target.style.background = displayMode === 'single' ? '#1A1A1A' : '#FFC94A';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = displayMode === 'single' ? '0px 6px 12px rgba(35, 34, 33, 0.5)' : '0px 6px 12px rgba(255, 210, 117, 0.5)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = displayMode === 'single' ? '#232221' : '#FFD275';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = displayMode === 'single' ? '0px 4px 8px rgba(35, 34, 33, 0.4)' : '0px 4px 8px rgba(255, 210, 117, 0.4)';
                    }}
                  >
                    {t('singlePhoto') || 'Foto Individual'}
                  </button>
                  <button
                    style={{
                      minWidth: isMobile ? '120px' : '140px',
                      height: '52px',
                      padding: '12px 16px',
                      background: displayMode === 'assembled' ? '#232221' : '#FFD275',
                      border: 'none',
                      borderRadius: '999px',
                      color: displayMode === 'assembled' ? 'white' : '#232221',
                      fontSize: '14px',
                      fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: '700',
                      lineHeight: '28px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '7px',
                      boxShadow: displayMode === 'assembled' ? '0px 4px 8px rgba(35, 34, 33, 0.4)' : '0px 4px 8px rgba(255, 210, 117, 0.4)',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleDisplayModeAndDownload('assembled')}
                    onMouseOver={(e) => {
                      e.target.style.background = displayMode === 'assembled' ? '#1A1A1A' : '#FFC94A';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = displayMode === 'assembled' ? '0px 6px 12px rgba(35, 34, 33, 0.5)' : '0px 6px 12px rgba(255, 210, 117, 0.5)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = displayMode === 'assembled' ? '#232221' : '#FFD275';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = displayMode === 'assembled' ? '0px 4px 8px rgba(35, 34, 33, 0.4)' : '0px 4px 8px rgba(255, 210, 117, 0.4)';
                    }}
                  >
                    {t('assembledPhotos') || 'Múltiples Fotos'}
                  </button>
                </div>
              </div>
            )}
            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
              {!scannedImageURL && (
                <button 
                  style={{
                    minWidth: isMobile ? '120px' : '140px',
                    height: '52px',
                    padding: '12px 16px',
                    background: '#232221',
                    border: 'none',
                    borderRadius: '999px',
                    color: 'white',
                    fontSize: '20px',
                    fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontWeight: '700',
                    lineHeight: '28px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '7px',
                    boxShadow: '0px 4px 8px rgba(35, 34, 33, 0.4)',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={onRetake}
                  onMouseOver={(e) => {
                    e.target.style.background = '#1A1A1A';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0px 6px 12px rgba(35, 34, 33, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#232221';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0px 4px 8px rgba(35, 34, 33, 0.4)';
                  }}
                >
                  Volver
                </button>
              )}
              {!scannedImageURL && (
                <button 
                  style={{
                    minWidth: isMobile ? '120px' : '140px',
                    height: '52px',
                    padding: '12px 16px',
                    background: '#232221',
                    border: 'none',
                    borderRadius: '999px',
                    color: 'white',
                    fontSize: '20px',
                    fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontWeight: '700',
                    lineHeight: '28px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '7px',
                    boxShadow: '0px 4px 8px rgba(35, 34, 33, 0.4)',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={onUpload}
                  onMouseOver={(e) => {
                    e.target.style.background = '#1A1A1A';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0px 6px 12px rgba(35, 34, 33, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#232221';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0px 4px 8px rgba(35, 34, 33, 0.4)';
                  }}
                >
                  Procesar
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
